import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";

const NAV = [
    { label: "Shop", href: "#shop" },
    { label: "Worn", href: "#worn" },
    { label: "Story", href: "#story" },
    { label: "Connect", href: "#contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const { count, setOpen: setCartOpen } = useCart();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled ? "glass" : "bg-transparent"
            }`}
        >
            <div className="max-w-[1600px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="flex items-center gap-2 group"
                >
                    <span className="w-2 h-2 bg-white block group-hover:bg-signal transition-colors" />
                    <span className="font-mono text-xs tracking-[0.3em] uppercase">
                        Energy Matcher
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-10">
                    {NAV.map((n) => (
                        <a
                            key={n.href}
                            href={pathname === "/" ? n.href : `/${n.href}`}
                            data-testid={`nav-${n.label.toLowerCase()}`}
                            className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground hover:text-white transition-colors relative group"
                        >
                            {n.label}
                            <span className="absolute -bottom-1 left-0 w-0 h-px bg-white transition-all group-hover:w-full" />
                        </a>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-6">
                    <Link
                        to="/admin"
                        data-testid="nav-admin"
                        className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground hover:text-white"
                    >
                        Admin
                    </Link>
                    <button
                        onClick={() => setCartOpen(true)}
                        data-testid="nav-cart"
                        aria-label="Open cart"
                        className="relative inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] hover:text-white"
                    >
                        <ShoppingBag size={14} />
                        <span>Bag</span>
                        {count > 0 && (
                            <span
                                data-testid="cart-count"
                                className="ml-1 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 bg-white text-black text-[10px] font-mono"
                            >
                                {count}
                            </span>
                        )}
                    </button>
                    <a
                        href="#shop"
                        data-testid="nav-cta"
                        className="font-mono text-[10px] uppercase tracking-[0.25em] px-4 py-2 border border-white/30 hover:bg-white hover:text-black transition-all"
                    >
                        Shop The Drop
                    </a>
                </div>

                <div className="md:hidden flex items-center gap-3">
                    <button
                        onClick={() => setCartOpen(true)}
                        data-testid="nav-cart-mobile"
                        aria-label="Open cart"
                        className="relative p-2 text-white"
                    >
                        <ShoppingBag size={18} />
                        {count > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 bg-white text-black text-[9px] font-mono flex items-center justify-center">
                                {count}
                            </span>
                        )}
                    </button>
                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen(!open)}
                        className="text-white"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="md:hidden glass overflow-hidden"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="px-6 py-6 flex flex-col gap-6">
                            {NAV.map((n) => (
                                <a
                                    key={n.href}
                                    href={n.href}
                                    onClick={() => setOpen(false)}
                                    className="font-display text-3xl uppercase tracking-tight"
                                >
                                    {n.label}
                                </a>
                            ))}
                            <Link
                                to="/admin"
                                className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground"
                            >
                                Admin Panel
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
